import { Phone, Mail, MapPin } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

export default function SiteFooter() {
  return (
    <footer className="bg-dark-green text-beige">
      <div className="mx-auto w-full max-w-7xl px-6 py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          {/* Column 1: Logo and About */}
          <div className="md:col-span-4">
            <img src="/logo.webp" alt="Pineridge Hotel Logo" className="h-14" />
            <p className="mt-4 text-sm text-beige">
              Pineridge Hotel blends heritage charm with warm hospitality, peaceful garden spaces, and a prime Darjeeling location, offering guests a comfortable and memorable stay.
            </p>
            
          </div>

          {/* Column 2: Quick Links */}
          <div className="md:col-span-2">
            <h3 className="text-base font-semibold text-beige tracking-wider uppercase">Quick Links</h3>
            <ul className="mt-4 space-y-3">
              <FooterLink to="/">Home</FooterLink>
              <FooterLink to="/#rooms-and-suites">Rooms & Suites</FooterLink>
              <FooterLink to="/about-us">About Us</FooterLink>
              <FooterLink to="/contact-us">Contact Us</FooterLink>
              <FooterLink to="/gallery">Gallery</FooterLink>
              <FooterLink to="/experiences">Experiences</FooterLink>
            </ul>
          </div>

          {/* Column 3: Legal */}
          <div className="md:col-span-2">
            <h3 className="text-base font-semibold text-beige tracking-wider uppercase">Legal</h3>
            <ul className="mt-4 space-y-3">
              <FooterLink to="/terms-and-conditions">Terms & Conditions</FooterLink>
              <FooterLink to="/return-and-refund-policy">Return & Refund</FooterLink>
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div className="md:col-span-4">
            <h3 className="text-base font-semibold text-beige tracking-wider uppercase">Contact Us</h3>
            <div className="mt-4 space-y-4 text-sm text-beige">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 shrink-0 text-beige mt-1" />
                <div>
                  <div>+91 354 2254074</div>
                  <div>+91 93327 30219</div>
                  <p className="text-xs text-gray-400">(Available 8:00 AM – 10:00 PM)</p>
                </div>
              </div>
              <hr className="border-gray-600" />
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 shrink-0 text-beige mt-1" />
                <div>
                  <span>Reservations & General Queries:</span>
                  <a href="mailto:hotelpineridge@gmail.com" className="block hover:underline">hotelpineridge@gmail.com</a>
                </div>
              </div>
              <hr className="border-gray-600" />
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 shrink-0 text-beige mt-1" />
                <a href="https://maps.app.goo.gl/rimnn6hUMpsgay7f6" target="_blank" rel="noopener noreferrer">
                  <span>The Mall, 19, Nehru Rd, Chauk Bazaar, Darjeeling, West Bengal 734101</span>
                </a>
              </div>
              <hr className="border-gray-600" />
              <div>
                <h4 className="font-semibold">Operating Hours</h4>
                <p className="mt-2">Front Desk: 24×7</p>
                <p>Reservations Team: 8:00 AM – 10:00 PM</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-beige">
        <div className="mx-auto w-full max-w-7xl px-6 py-6 text-sm text-beige">
          <p>&copy; {new Date().getFullYear()} Pineridge Hotel. All rights reserved. Designed and Developed by <a href="https://kurukulle.com/" target="_blank" rel="noopener noreferrer" className="hover:underline">Kurukulle Software Solutions</a>.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLinkClick = (e) => {
    e.preventDefault();

    const isHashLink = to.includes('#');
    
    if (!isHashLink) {
      navigate(to);
      return;
    }

    const [path, hash] = to.split('#');
    const isHomePage = location.pathname === '/';

    if (isHomePage && (path === '' || path === '/')) {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(path || '/');
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100); // Adjust timeout as needed
    }
  };

  return (
    <li>
      <a href={to} onClick={handleLinkClick} className="text-beige transition hover:text-white hover:underline">
        {children}
      </a>
    </li>
  );
}
